import * as t from "io-ts";
import nullable from "./nullable";
import isoDateTimeForJSON from "./isoDateTimeForJSON";
import { realOptional as optional } from "./optional";
import { placeT } from "./place";
import { cargoTypeT } from "./types";
import { unitT } from "./cargo-unit";
export const createDriverSessionTypes = ({ decimal, date, dateTime, }) => {
    const nullablePositionT = nullable(t.strict({
        lat: t.number,
        lon: t.number,
        accuracy: t.number,
    }));
    const pickupEventT = t.strict({
        type: t.union([
            t.literal("PICKUP"),
            t.literal("DROPOFF"),
            t.literal("ARRIVED"),
            t.literal("DEPARTED"),
        ]),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        time: isoDateTimeForJSON,
    });
    const addCommentEventT = t.strict({
        type: t.literal("ADD_COMMENT"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        comment: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const editCommentEventT = t.strict({
        type: t.literal("EDIT_COMMENT"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        comment: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const deleteCommentEventT = t.strict({
        type: t.literal("DELETE_COMMENT"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const addWeightEventT = t.strict({
        type: t.literal("ADD_WEIGHT"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        weight: decimal,
        time: optional(isoDateTimeForJSON),
    });
    const editWeightEventT = t.strict({
        type: t.literal("EDIT_WEIGHT"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        weight: decimal,
        time: optional(isoDateTimeForJSON),
    });
    const deleteWeightEventT = t.strict({
        type: t.literal("DELETE_WEIGHT"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const addVolumeEventT = t.strict({
        type: t.literal("ADD_VOLUME"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        cubicMeters: decimal,
        time: optional(isoDateTimeForJSON),
    });
    const editVolumeEventT = t.strict({
        type: t.literal("EDIT_VOLUME"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        cubicMeters: decimal,
        time: optional(isoDateTimeForJSON),
    });
    const deleteVolumeEventT = t.strict({
        type: t.literal("DELETE_VOLUME"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const addInvoiceReferenceEventT = t.strict({
        type: t.literal("ADD_INVOICE_REFERENCE"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        ref: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const editInvoiceRefEventT = t.strict({
        type: t.literal("EDIT_INVOICE_REFERENCE"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        ref: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const deleteInvoiceRefEventT = t.strict({
        type: t.literal("DELETE_INVOICE_REFERENCE"),
        cargoId: t.string,
        stopId: t.string,
        position: nullablePositionT,
        id: t.string,
        time: optional(isoDateTimeForJSON),
    });
    const addTripEventT = t.strict({
        type: t.union([t.literal("START_TRIP"), t.literal("STOP_TRIP")]),
        note: optional(t.string),
        position: nullablePositionT,
        odometer: t.number,
        time: isoDateTimeForJSON,
    });
    const addEventT = t.union([
        pickupEventT,
        addCommentEventT,
        editCommentEventT,
        deleteCommentEventT,
        addWeightEventT,
        editWeightEventT,
        deleteWeightEventT,
        addVolumeEventT,
        editVolumeEventT,
        deleteVolumeEventT,
        addInvoiceReferenceEventT,
        editInvoiceRefEventT,
        deleteInvoiceRefEventT,
        addTripEventT,
    ]);
    const stopTypeT = t.union([t.literal("PICKUP"), t.literal("DROPOFF")]);
    const stopT = t.strict({
        id: t.string,
        cargo_id: t.string,
        type: stopTypeT,
        place: nullable(placeT),
        contact: nullable(t.string),
        phone: nullable(t.string),
        date: nullable(date),
        openingDate: nullable(date),
        openingTime: nullable(t.string),
        time: nullable(t.string),
        lm: decimal,
        pieces: nullable(t.array(t.strict({ unit: unitT, quantity: t.number }))),
        height: nullable(decimal),
        length: nullable(decimal),
        width: nullable(decimal),
        weight: nullable(decimal),
        colli: nullable(decimal),
        cubicMeters: nullable(decimal),
        temperature: nullable(t.string),
        tag: nullable(t.string),
        fixTime: t.boolean,
        ref: nullable(t.string),
        coord: t.strict({
            lat: t.number,
            lon: t.number,
        }),
    });
    const cargoT = t.strict({
        id: t.string,
        type: nullable(cargoTypeT),
        note: nullable(t.string),
        clientId: nullable(t.string),
        description: nullable(t.string),
        driverCanAddDropoffs: t.boolean,
        undeliveredLm: decimal,
        noOfDropoffs: t.number,
        revision: t.string,
        createdAt: dateTime,
        createdByDriver: t.boolean,
        hasInvoiceRef: t.boolean,
        size: nullable(t.strict({
            unitId: unitT,
            quantity: decimal,
        })),
    });
    const clientT = t.strict({
        id: t.string,
        name: t.string,
        showArrivalDepartureToDriver: nullable(t.boolean),
        showAddCubicMetersToDriver: nullable(t.boolean),
    });
    const routeCargoStopT = t.strict({
        type: t.literal("CARGO_STOP"),
        stop: stopT,
    });
    const routeWaypointT = t.strict({
        id: t.string,
        type: t.literal("WAYPOINT"),
        waypointType: t.union([
            t.literal("AUTO_FIRST_STOP"),
            t.literal("MANUAL"),
            t.literal("FROM_START_ACTIVITY"),
            t.literal("FROM_END_ACTIVITY"),
        ]),
        note: nullable(t.string),
        place: placeT,
    });
    const routeStopT = t.union([routeCargoStopT, routeWaypointT]);
    const sessionResponseT = t.strict({
        routeStops: t.array(routeStopT),
        stops: t.array(stopT),
        cargos: t.array(cargoT),
        clients: t.array(clientT),
        createdByDispatcher: t.boolean,
    });
    const attachmentT = t.strict({
        cargoId: t.string,
        stopId: nullable(t.string),
        fileId: t.string,
        documentType: t.string,
    });
    const sessionT = t.strict({
        id: t.string,
        started: t.boolean,
        stopped: t.boolean,
        stops: t.record(t.string, t.strict({
            id: t.string,
            weight: nullable(decimal),
            cubicMeters: nullable(decimal),
            completedAt: nullable(date),
            arrivedAt: nullable(date),
            departedAt: nullable(date),
            refs: t.array(t.strict({
                createdAt: date,
                ref: t.string,
            })),
            comments: t.array(t.strict({
                createdAt: date,
                comment: t.string,
            })),
        })),
        attachments: t.array(attachmentT),
    });
    const sessionsT = t.array(sessionT);
    const addFileReferenceT = t.strict({
        cargoId: t.string,
        stopId: t.string,
        position: nullable(t.strict({
            lat: t.number,
            lon: t.number,
            accuracy: t.number,
        })),
        file: t.strict({
            documentType: t.union([t.literal("POD"), t.literal("IMAGE")]),
            name: t.string,
            mimeType: t.string,
            s3Identifier: t.string,
        }),
        time: dateTime,
    });
    const addFileReferenceWithTrailerT = t.intersection([
        addFileReferenceT,
        t.strict({
            trailerId: t.string,
        }),
    ]);
    const deleteFileReferenceT = t.strict({
        trailerId: t.string,
        position: nullable(t.strict({
            lat: t.number,
            lon: t.number,
            accuracy: t.number,
        })),
        time: dateTime,
    });
    return {
        addEventT,
        cargoT,
        clientT,
        stopT,
        routeStopT,
        routeCargoStopT,
        routeWaypointT,
        sessionResponseT,
        attachmentT,
        sessionT,
        sessionsT,
        addFileReferenceT,
        addFileReferenceWithTrailerT,
        deleteFileReferenceT,
    };
};
//# sourceMappingURL=driver-sessions.js.map